import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileSearch, Download, Clock } from "lucide-react";
import { publicBgcFee, publicBgcOrder, publicBgcStatus } from "../services/ujmsApi";
import { PublicServiceHeader } from "./PublicServiceHeader";
import { Link } from "react-router-dom";

const err = (e, f) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);
const inp = "w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40";
const label = "text-[12px] font-semibold text-slate-600 mb-0.5 block";
const card = "bg-white rounded-md shadow-sm border border-slate-200 p-5";
const btn = "inline-flex items-center gap-1.5 bg-[#0b2d52] text-white rounded-sm px-3 py-1.5 text-sm font-semibold hover:bg-[#123f72] disabled:opacity-50";
const btnG = "inline-flex items-center gap-1.5 border border-slate-300 rounded-sm px-3 py-1.5 text-sm font-medium hover:bg-slate-50";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/ujms`;

export default function PublicBackgroundCheck() {
  const [tab, setTab] = useState("order");
  const [fee, setFee] = useState(null);
  useEffect(() => { publicBgcFee().then((r) => setFee(r.data)).catch(() => {}); }, []);
  return (
    <div className="court-public-shell min-h-screen" data-testid="public-bgc-page">
      <PublicServiceHeader title="Illinois State Police" subtitle="Criminal-history access & review · Bureau of Identification" icon={ShieldCheck} testId="background-service-header" />
      <nav className="court-public-nav" aria-label="Public services" data-testid="background-public-navigation"><Link to="/portal" data-testid="background-court-records">Court records</Link><Link to="/background-check" data-testid="background-home">Background checks</Link><Link to="/foid" data-testid="background-foid-service">FOID services</Link></nav>
      <main className="max-w-3xl mx-auto px-5 py-6 space-y-4">
        <div className="flex gap-1 border-b border-slate-300">
          <button className={`px-3 py-2 text-sm font-semibold ${tab === "order" ? "border-b-2 border-[#0b2d52] text-[#0b2d52]" : "text-slate-500"}`} onClick={() => setTab("order")} data-testid="pubbgc-tab-order">Order a report</button>
          <button className={`px-3 py-2 text-sm font-semibold ${tab === "status" ? "border-b-2 border-[#0b2d52] text-[#0b2d52]" : "text-slate-500"}`} onClick={() => setTab("status")} data-testid="pubbgc-tab-status">Pay &amp; download</button>
        </div>
        {fee && <div className="text-[12.5px] text-slate-600 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="pubbgc-fee">Fee: <b>${fee.base_fee.toFixed(2)}</b> base (covers the first {fee.free_pages} pages) + <b>${fee.per_page.toFixed(2)}</b> per additional page. Paid in cash at the ISP clerk's window, or waived by a clerk.</div>}
        {tab === "order" ? <OrderView /> : <StatusView />}
      </main>
    </div>
  );
}

function OrderView() {
  const [f, setF] = useState({ first_name: "", last_name: "", dob: "", email: "", purpose: "Personal background check" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async () => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.dob.trim()) { toast.error("Enter your name and date of birth"); return; }
    setBusy(true);
    try { const r = await publicBgcOrder(f); setDone(r.data); toast.success(`Report ready — request ${r.data.request_number}`); }
    catch (e) { err(e, "Could not place the order"); }
    finally { setBusy(false); }
  };
  if (done) return (
    <div className={card} data-testid="pubbgc-order-done">
      <div className="flex items-center gap-2 text-[#0b2d52] font-bold mb-2"><FileSearch className="w-5 h-5" /> Report generated</div>
      <div className="text-sm space-y-1">
        <div>Request number: <b className="font-mono" data-testid="pubbgc-request-number">{done.request_number}</b></div>
        <div>Length: <b>{done.pages}</b> page(s)</div>
        <div>Fee due: <b>${done.fee.toFixed(2)}</b></div>
      </div>
      <div className="text-[12.5px] text-slate-600 mt-3 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">{done.message}</div>
      <div className="mt-3 text-[12.5px]">Keep your request number and date of birth. Once a clerk records your cash payment (or waives the fee), use the <b>Pay &amp; download</b> tab to retrieve your report.</div>
    </div>
  );
  return (
    <div className={card} data-testid="pubbgc-order-form">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>First name</label><input className={inp} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid="pubbgc-first" /></div>
        <div><label className={label}>Last name</label><input className={inp} value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid="pubbgc-last" /></div>
        <div><label className={label}>Date of birth</label><input type="date" className={inp} value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="pubbgc-dob" /></div>
        <div><label className={label}>Email (optional)</label><input className={inp} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="pubbgc-email" /></div>
      </div>
      <div className="mt-3"><label className={label}>Purpose</label><input className={inp} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="pubbgc-purpose" /></div>
      <div className="mt-4"><button className={btn} onClick={submit} disabled={busy} data-testid="pubbgc-order-submit"><FileSearch className="w-4 h-4" /> {busy ? "Generating…" : "Generate my report & fee"}</button></div>
    </div>
  );
}

function StatusView() {
  const [f, setF] = useState({ request_number: "", dob: "", code: "" });
  const [r, setR] = useState(null);
  const check = async () => {
    if (!f.request_number.trim() || !f.dob.trim()) { toast.error("Enter your request number and date of birth"); return; }
    try { const res = await publicBgcStatus({ request_number: f.request_number, dob: f.dob }); setR(res.data); }
    catch (e) { setR(null); err(e, "Not found"); }
  };
  const download = () => {
    const u = `${API}/public/background-check/${encodeURIComponent(f.request_number.trim().toUpperCase())}/pdf?dob=${encodeURIComponent(f.dob.trim())}${f.code ? `&code=${encodeURIComponent(f.code.trim().toUpperCase())}` : ""}`;
    window.open(u, "_blank");
  };
  return (
    <div className={card} data-testid="pubbgc-status-form">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={label}>Request number</label><input className={inp} value={f.request_number} placeholder="PBC-2026-000001" onChange={(e) => setF({ ...f, request_number: e.target.value })} data-testid="pubbgc-status-req" /></div>
        <div><label className={label}>Date of birth</label><input type="date" className={inp} value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="pubbgc-status-dob" /></div>
      </div>
      <div className="mt-3"><button className={btnG} onClick={check} data-testid="pubbgc-status-check"><Clock className="w-4 h-4" /> Check status</button></div>
      {r && (
        <div className="mt-4 border-t border-slate-200 pt-3 text-sm" data-testid="pubbgc-status-result">
          <div>Name: <b>{r.name}</b> · {r.pages} page(s) · fee <b>${r.fee.toFixed(2)}</b></div>
          <div className="mt-1">Status: <b className={r.ready ? "text-emerald-700" : "text-amber-700"}>{r.status.replace(/_/g, " ")}</b>{r.payment_type ? ` (${r.payment_type})` : ""}</div>
          {!r.ready && <div className="text-[12.5px] text-slate-600 mt-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">Payment has not yet been authorized. Pay in cash at the ISP clerk's window (or ask for a waiver). The clerk will give you an authorization code.</div>}
          {r.ready && (
            <div className="mt-3 space-y-2">
              {r.needs_code && <div><label className={label}>Authorization code (from the clerk)</label><input className={inp} value={f.code} placeholder="AUTH-XXXXXX" onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="pubbgc-status-code" /></div>}
              <button className={btn} onClick={download} data-testid="pubbgc-download"><Download className="w-4 h-4" /> Download my report</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
